import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LocalFloristOutlinedIcon from "@mui/icons-material/LocalFloristOutlined";
import Slider from "react-slick";

import { getCompanyInfo } from "api/Company-info";
import { useTranslation } from "i18n";
import useApi from "hooks/useApi";

import "./style.css";

const teamMemberTranslationKeys = {
  "Aniel B.": "anielB",
  "Shivani B.": "shivaniB",
  "Shiva B.": "shivaB",
  "Rishi S.": "rishiS",
  // Afiek B. is no longer with the company. Uncomment if he rejoins.
  // "Afiek B.": "afiekB",
  "Coretta C.": "corettaC",
  "Warsha B.": "warshaB",
  "August I.": "augustI",
  "Just M.": "justM",
};

const departmentTranslationKeys = {
  "Executive Board": "executiveBoard",
};

// Afiek B. left the company. His profile stays in the company data and
// translations so it can be shown again if he rejoins.
const HIDDEN_TEAM_MEMBERS = new Set(["Afiek B."]);

const isVisibleTeamMember = (member) => !HIDDEN_TEAM_MEMBERS.has(member?.name);

const ORDER_AFTER_RISHI = ["August", "Marlene", "Warsha", "Coretta"];

const memberFirstName = (name = "") =>
  name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .split(" ")
    .filter(Boolean)[0]
    ?.toLowerCase() || "";

const orderTeamMembers = (members) => {
  const rishiIndex = members.findIndex(
    (member) => memberFirstName(member?.name) === "rishi",
  );
  if (rishiIndex === -1) return members;

  const before = members.slice(0, rishiIndex + 1);
  const after = members.slice(rishiIndex + 1);
  const placed = new Set();
  const ordered = ORDER_AFTER_RISHI.map((firstName) =>
    after.find((member) => memberFirstName(member?.name) === firstName.toLowerCase()),
  ).filter(Boolean);

  ordered.forEach((member) => placed.add(member));

  return [...before, ...ordered, ...after.filter((member) => !placed.has(member))];
};

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

function TimelineStep({ item, index }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const year = item.date.match(/\d{4}/)?.[0];
  const month = item.date.replace(/\d{4}/g, "").trim();

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { threshold: 0.4, rootMargin: "0px 0px -8% 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <li
      ref={ref}
      className={`about-timeline__item${inView ? " is-in" : ""}${
        index % 2 ? " is-alt" : ""
      }`}
      style={{ "--step": index }}
    >
      <div className="about-timeline__rail" aria-hidden="true">
        <span className="about-timeline__dot" />
      </div>
      <article className="about-timeline__card">
        <div className="about-timeline__when">
          {year && <b>{year}</b>}
          <strong>{month || item.date}</strong>
        </div>
        <p>{item.description}</p>
      </article>
    </li>
  );
}

function AboutUs() {
  const { t } = useTranslation();
  const companyInfo = useApi(getCompanyInfo);
  const sliderRef = useRef(null);
  const [openLogin, setOpenLogin] = useState(false);
  const [initialSlide, setInitialSlide] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [openCount, setOpenCount] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    companyInfo.request();
  }, []);

  const getTranslatedValue = (key, fallback) => {
    const translated = t(key);
    return translated === key ? fallback : translated;
  };

  const getTranslatedDepartment = (department) => {
    const departmentKey = departmentTranslationKeys[department];
    if (!departmentKey) return department;
    return getTranslatedValue(
      `aboutUs.team.departments.${departmentKey}`,
      department,
    );
  };

  const getTranslatedMemberField = (member, field) => {
    const memberKey = teamMemberTranslationKeys[member?.name];
    if (!memberKey) return member?.[field];
    return getTranslatedValue(
      `aboutUs.team.members.${memberKey}.${field}`,
      member?.[field],
    );
  };

  const history = [
    "october2007",
    "december2007",
    "april2010",
    "may2011",
    "august2013",
    "december2021",
    "october2022",
    "march2023",
  ].map((key) => ({
    date: t(`aboutUs.history.${key}.date`),
    description: t(`aboutUs.history.${key}.description`),
  }));

  const teamsData = companyInfo?.data?.teams || [];
  const activeTeams = teamsData
    .map((team) => ({
      ...team,
      members: orderTeamMembers(
        (team?.members || []).filter(
          (m) => !m?.hasPassedAway && isVisibleTeamMember(m),
        ),
      ),
    }))
    .filter((team) => team.members.length > 0);
  const memoriamMembers = teamsData.flatMap((team) =>
    (team?.members || []).filter(
      (m) => m?.hasPassedAway && isVisibleTeamMember(m),
    ),
  );
  const sliderMembers = [
    ...activeTeams.flatMap((team) => team.members),
    ...memoriamMembers,
  ];
  const currentMember = sliderMembers[currentSlide];
  const isMemoriam = !!currentMember?.hasPassedAway;

  const settings = {
    infinite: sliderMembers.length > 1,
    speed: 450,
    arrows: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide,
    fade: true,
    swipe: sliderMembers.length > 1,
    accessibility: true,
    afterChange: (idx) => setCurrentSlide(idx),
  };

  const openBio = (member) => {
    const idx = sliderMembers.indexOf(member);
    const safeIdx = idx >= 0 ? idx : 0;
    setInitialSlide(safeIdx);
    setCurrentSlide(safeIdx);
    setOpenCount((c) => c + 1);
    setOpenLogin(true);
  };

  return (
    <div className="about-page">
      <section className="about-hero">
        <div className="about-hero__glow" aria-hidden="true" />
        <div className="container about-hero__content">
          <p className="about-kicker about-kicker--light">
            {t("aboutUs.heroEyebrow")}
          </p>
          <h1>{t("aboutUs.heroTitle")}</h1>
          <p className="about-hero__subtitle">{t("aboutUs.heroSubtitle")}</p>
        </div>
      </section>

      <div className="container about-shell">
        {companyInfo.loading ? (
          <div className="about-team-grid">
            {Array.from({ length: 6 }).map((_, index) => (
              <div className="about-person is-skeleton" key={index}>
                <Skeleton variant="circular" width={92} height={92} />
                <Skeleton variant="text" width="70%" />
                <Skeleton variant="text" width="50%" />
              </div>
            ))}
          </div>
        ) : activeTeams.length === 0 && memoriamMembers.length === 0 ? (
          <div className="about-empty">{t("common.noDataFound")}</div>
        ) : (
          activeTeams.map((team) => (
            <section
              className="about-team"
              key={team?.department || team?._id}
            >
              <div className="about-section-head">
                <p className="about-kicker">{t("aboutUs.team.eyebrow")}</p>
                <h2>{getTranslatedDepartment(team?.department)}</h2>
              </div>
              <div className="about-team-grid">
                {team.members.map((employee) => (
                  <button
                    type="button"
                    className="about-person"
                    key={employee?.name}
                    onClick={() => openBio(employee)}
                  >
                    <div className="about-person__photo">
                      {employee?.image ? (
                        <img src={employee.image} alt={employee?.name || ""} />
                      ) : (
                        <span>{initials(employee?.name)}</span>
                      )}
                    </div>
                    <div className="about-person__meta">
                      <h3>{employee?.name}</h3>
                      <p>{getTranslatedMemberField(employee, "title")}</p>
                      <em>{t("aboutUs.readBio")} →</em>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          ))
        )}

        {memoriamMembers.length > 0 && (
          <section className="about-memory">
            <LocalFloristOutlinedIcon />
            <p className="about-kicker">{t("aboutUs.inMemoriam.eyebrow")}</p>
            <h2>{t("aboutUs.inMemoriam.title")}</h2>
            <p className="about-memory__intro">
              {t("aboutUs.inMemoriam.subtitle")}
            </p>
            <div className="about-memory__grid">
              {memoriamMembers.map((member) => (
                <button
                  type="button"
                  className="about-memory-card"
                  key={member?.name}
                  onClick={() => openBio(member)}
                >
                  <div className="about-memory-card__photo">
                    {member?.image ? (
                      <img src={member.image} alt={member?.name || ""} />
                    ) : (
                      <span>{initials(member?.name)}</span>
                    )}
                  </div>
                  <span className="about-memory-card__badge">
                    <LocalFloristOutlinedIcon />
                    {t("aboutUs.inMemoriam.badge")}
                  </span>
                  <h3>{member?.name}</h3>
                  <p>{getTranslatedMemberField(member, "title")}</p>
                  <em>{t("aboutUs.inMemoriam.readLegacy")} →</em>
                </button>
              ))}
            </div>
          </section>
        )}
      </div>

      <section className="about-beliefs">
        <div className="container about-beliefs__grid">
          <article>
            <p className="about-kicker about-kicker--light">01</p>
            <h2>{t("aboutUs.vision.title")}</h2>
            <p>{t("aboutUs.vision.description")}</p>
          </article>
          <article>
            <p className="about-kicker about-kicker--light">02</p>
            <h2>{t("aboutUs.mission.title")}</h2>
            <p>{t("aboutUs.mission.description")}</p>
          </article>
        </div>
      </section>

      <section className="container about-story">
        <div className="about-section-head">
          <p className="about-kicker">{t("aboutUs.storyEyebrow")}</p>
          <h2>{t("aboutUs.storyTitle")}</h2>
          <p>{t("aboutUs.companyStory")}</p>
        </div>
        <ol className="about-timeline">
          {history.map((item, index) => (
            <TimelineStep key={item.date} item={item} index={index} />
          ))}
        </ol>
      </section>

      <section className="container about-features">
        <article>
          <h3>{t("aboutUs.features.passportScanner.title")}</h3>
          <p>{t("aboutUs.features.passportScanner.description")}</p>
        </article>
        <article>
          <h3>{t("aboutUs.features.virtualReality.title")}</h3>
          <p>{t("aboutUs.features.virtualReality.note")}</p>
        </article>
      </section>

      <Modal
        open={openLogin}
        onClose={() => setOpenLogin(false)}
        closeAfterTransition
      >
        <Fade in={openLogin} timeout={280}>
          <Box className={`about-bio${isMemoriam ? " is-memory" : ""}`}>
            <div className="about-bio__banner" />
            <IconButton
              className="about-bio__close"
              onClick={() => setOpenLogin(false)}
              aria-label={t("common.close")}
            >
              <CloseIcon />
            </IconButton>
            <div className="about-bio__avatar">
              {currentMember?.image ? (
                <img
                  src={currentMember.image}
                  alt={currentMember?.name || ""}
                />
              ) : (
                <span>{initials(currentMember?.name)}</span>
              )}
            </div>
            <div className="about-bio__body">
              <Slider ref={sliderRef} key={openCount} {...settings}>
                {sliderMembers.map((member) => (
                  <div key={member?.name}>
                    {member?.hasPassedAway && (
                      <span className="about-memory-card__badge">
                        <LocalFloristOutlinedIcon />
                        {t("aboutUs.inMemoriam.badge")}
                      </span>
                    )}
                    <h3>{member?.name}</h3>
                    <p className="about-bio__role">
                      {getTranslatedMemberField(member, "title")}
                    </p>
                    <p className="about-bio__text">
                      {getTranslatedMemberField(member, "biography")}
                    </p>
                  </div>
                ))}
              </Slider>
            </div>
            {sliderMembers.length > 1 && (
              <div className="about-bio__nav">
                <IconButton
                  onClick={() => sliderRef.current?.slickPrev()}
                  aria-label="previous"
                >
                  <ChevronLeftIcon />
                </IconButton>
                <span>
                  {String(currentSlide + 1).padStart(2, "0")} /{" "}
                  {String(sliderMembers.length).padStart(2, "0")}
                </span>
                <IconButton
                  onClick={() => sliderRef.current?.slickNext()}
                  aria-label="next"
                >
                  <ChevronRightIcon />
                </IconButton>
              </div>
            )}
          </Box>
        </Fade>
      </Modal>
    </div>
  );
}

export default AboutUs;
